import React from "react";
import { useQuery } from "react-query";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  TextField,
  Typography,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { getUploadedDocuments, deleteUploadedDocument } from "../../../../PatientCare/Patientregistration/v2/registrationServices";

interface PatientDocumentsModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string | number;
  patientName?: string;
}

const PatientDocumentsModal: React.FC<PatientDocumentsModalProps> = ({
  open,
  onClose,
  patientId,
  patientName,
}) => {
  const { data: documentsResponse, isLoading, refetch } = useQuery(
    ["patientDocuments", patientId],
    () => getUploadedDocuments(String(patientId)),
    {
      enabled: open && !!patientId,
    }
  );

  const documents = documentsResponse?.response || [];

  const handleDelete = async (docId: number | string) => {
    if (window.confirm("Are you sure you want to delete this document?")) {
      try {
        const res = await deleteUploadedDocument(docId);
        if (res.responseIndicator === "success") {
          refetch();
        } else {
          alert(res.responseMessage || "Failed to delete document");
        }
      } catch (error) {
        console.error("Error deleting document:", error);
        alert("An error occurred while deleting the document");
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">Documents - {patientName || patientId}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={30} />
          </Box>
        ) : documents.length > 0 ? (
          <List>
            {documents.map((doc, index) => (
              <React.Fragment key={doc.id || index}>
                <ListItem sx={{ flexDirection: "column", alignItems: "stretch", py: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {doc.fileName}
                    </Typography>
                    <Box>
                      <Tooltip title="View Document">
                        <IconButton
                          size="small"
                          component="a"
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="primary"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(doc.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    label="Document Name"
                    value={doc.documentType || ""}
                    disabled
                  />
                </ListItem>
                {index < documents.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="textSecondary">No documents found for this patient.</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientDocumentsModal;
